import type { BaseQueryApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { AxiosRequestConfigExtended } from '@vision/ui/interfaces';
import {
  failureUploadProgress,
  getNextFileId,
  removeUploadFileProgress,
  removeUploadProgress,
  startUploadProgress,
  successUploadProgress,
  updateUploadProgress,
} from '@vision/ui/store/states/upload-progress-state/upload-progress-state';
import type { RootState } from '@vision/ui/store/store';
import { activityTracker } from '@vision/ui/utils/activity-tracker';
import { ENV } from '@vision/ui/utils/env.utils';
import { uuid } from '@vision/ui/utils/uuid.utils';
import { createHttpEvent } from '@vision/ui/window-events/http.event';
import axios, { AxiosError, AxiosProgressEvent, AxiosResponse, CanceledError } from 'axios';

const dispatchNotificationHttpEvent = createHttpEvent('dispatchHttpNotification');

function trackUploadProgress(
  dispatch: BaseQueryApi['dispatch'],
  event: AxiosProgressEvent,
  uploadTrackId: string,
  fileId: number,
) {
  const { loaded, total } = event;
  const progress = Math.floor((loaded / total) * 100);

  dispatch(updateUploadProgress({ id: uploadTrackId, progress, fileId }));
}

export const axiosBaseQuery =
  (baseUrl = ENV.API_URL): BaseQueryFn<AxiosRequestConfigExtended, unknown, AxiosResponse> =>
  async (
    {
      url,
      method,
      data,
      params,
      responseType,
      uploadTrackId,
      fileFieldName = 'file',
      extraOptions,
      adapter,
      fetchOptions,
    },
    { abort, dispatch, getState, signal },
  ) => {
    const state = getState() as RootState;
    const { selectedAccountId, token } = state.AuthState;
    const isUpload = !!uploadTrackId;
    const fileId = isUpload ? getNextFileId(state.UploadProgressState.progress, uploadTrackId) : null;

    if (isUpload) {
      const listener = () => {
        dispatch(removeUploadProgress({ id: uploadTrackId }));
        signal.removeEventListener('abort', listener);
      };
      signal.addEventListener('abort', listener);

      const formData = data as FormData;
      const files = formData.getAll(fileFieldName) as File | File[];
      dispatch(
        startUploadProgress({
          abort,
          files,
          id: uploadTrackId,
          remove: () => dispatch(removeUploadFileProgress({ id: uploadTrackId, fileId })),
        }),
      );
    }

    try {
      const result = await axios({
        url: baseUrl + url,
        method,
        data,
        params,
        responseType,
        signal,
        adapter,
        fetchOptions,
        ...(isUpload
          ? {
              onUploadProgress: (event: AxiosProgressEvent) =>
                trackUploadProgress(dispatch, event, uploadTrackId, fileId),
            }
          : {}),
        headers: {
          ...(token ? { Authorization: `Token token="${token}"` } : {}),
          ...(selectedAccountId ? { 'x-node-id': selectedAccountId } : {}),
          'x-correlation-id': uuid(),
        },
      });

      if (isUpload) {
        dispatch(successUploadProgress({ id: uploadTrackId, fileId }));
      }

      if (token) {
        activityTracker.markApiCall();
      }

      dispatchNotificationHttpEvent({
        extraOptions,
        meta: result,
      });

      return result;
    } catch (axiosError) {
      const err = axiosError as AxiosError | CanceledError<unknown>;

      dispatchNotificationHttpEvent({
        extraOptions,
        meta: err,
      });

      if (isUpload) {
        dispatch(failureUploadProgress({ id: uploadTrackId, fileId }));
      }
      return {
        data: null,
        error: (err.response || err) as AxiosResponse,
        meta: null,
      };
    }
  };
